import { libraryFunctions } from './resources/price'
import { roundToTwoDecimals } from '../utils/math'
import { Product } from '../utils/types/Product'
import { CONSUMPTION_TAKE_RATE_TIERS } from '../utils/constants'
import { getIsImportedProduct } from './product'

const { getTakeRatePercentage, getDefaultPrice } = libraryFunctions

export interface CalculateTakeRateParams {
  product: Product
  selectedQuantity: number
  consumptionTier: number
  cartTotal: number
  exchangeRate: number
  isTakeRateEnabled: boolean
  excludeAmount?: number
}

export interface CalculateTakeRateResult {
  product: Product
  prices: Product['price']
}

/**
 * Calculate take rate adjustments for a product based on consumption tier
 * @param params - Parameters for take rate calculation
 * @returns Modified product with adjusted prices
 */
export const calculateTakeRate = ({
  product,
  selectedQuantity,
  consumptionTier,
  cartTotal,
  exchangeRate,
  isTakeRateEnabled,
  excludeAmount = 0,
}: CalculateTakeRateParams): CalculateTakeRateResult => {
  if (!isTakeRateEnabled || getIsImportedProduct(product)) {
    return {
      product,
      prices: product?.price,
    }
  }

  const defaultPriceObj = getDefaultPrice(product.price)
  const defaultPrice = Number(defaultPriceObj?.['price.value']) + Number(defaultPriceObj?.['price.rebate'])

  const tier = (
    consumptionTier
    + (defaultPrice * selectedQuantity * (1 / exchangeRate))
    + (cartTotal * (1 / exchangeRate))
    - (excludeAmount * (1 / exchangeRate))
  )

  const prices = product?.price.map((price) => {
    // Fill up to minimum quantity as lower quantity is not possible
    const moqFillUp = selectedQuantity < price['price.quantity']
      ? (defaultPrice * (Number(price['price.quantity']) - selectedQuantity)) / exchangeRate
      : 0
    const maxPrice = Number(price['price.value']) + Number(price['price.rebate'])
    const takeRate = getTakeRatePercentage(tier + moqFillUp)
    const maxPercentage = Math.max(...Object.values(CONSUMPTION_TAKE_RATE_TIERS))
    // First remove take rate added in BE to be able to change it
    const priceWithoutTakeRate = price['price.listprice']
      ? Number(price['price.listprice'])
      : maxPrice * (1 - maxPercentage)

    // Then add take rate back with new rate
    const priceWithTakeRate = roundToTwoDecimals(priceWithoutTakeRate / (1 - takeRate))
    return {
      ...price,
      'price.label': `${price['price.label']} - take rate tier ${tier + moqFillUp}`,
      'price.value': priceWithTakeRate.toFixed(2),
      'price.rebate': roundToTwoDecimals(priceWithoutTakeRate / (1 - maxPercentage) - priceWithTakeRate).toFixed(2),
    }
  })

  return {
    prices,
    product: {
      ...product,
      price: prices,
    },
  }
}
